import React, { useState } from "react";
import { FeatureToggleDebugPanel } from "../FeatureToggleDebugPanel";
import "./FeatureToggleDebugTrigger.css";

interface FeatureToggleDebugTriggerProps {
  /**
   * Whether to show the debug trigger
   * Typically should be false in production
   */
  enabled?: boolean;
}

export const FeatureToggleDebugTrigger: React.FC<
  FeatureToggleDebugTriggerProps
> = ({ enabled = process.env.NODE_ENV !== "production" }) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Don't render anything in production or when disabled
  if (!enabled) {
    return null;
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsPanelOpen(true);
    }
  };

  return (
    <>
      <button
        className="feature-toggle-debug-trigger"
        onClick={() => setIsPanelOpen(true)}
        onKeyDown={handleKeyPress}
        title="Open Feature Toggle Debug Panel (Ctrl+Shift+F)"
        aria-label="Open Feature Toggle Debug Panel"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 21-4.35-4.35" />
          <path d="M9 17h6" />
          <path d="M17 9v8" />
        </svg>
        <span className="feature-toggle-debug-trigger__label">FT</span>
      </button>

      <FeatureToggleDebugPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
      />
    </>
  );
};

// Global keyboard shortcut handler
export const setupFeatureToggleShortcuts = (
  openPanel: () => void,
  enabled: boolean = process.env.NODE_ENV !== "production",
) => {
  if (!enabled) return () => {};

  const handleKeyDown = (event: KeyboardEvent) => {
    // Ctrl+Shift+F to open feature toggle panel
    if (event.ctrlKey && event.shiftKey && event.key === "F") {
      event.preventDefault();
      openPanel();
    }
  };

  document.addEventListener("keydown", handleKeyDown);

  return () => {
    document.removeEventListener("keydown", handleKeyDown);
  };
};
